import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { CONSTANT, wheelDataSetups } from './core/common';
import { BaseComponent } from './core/components/base.component';
import { ISearchedMemberModel } from './core/model';
import { FirebaseService, WheelSetupsService } from './core/service';
import {
  LoggedInAction,
  logoutAction,
} from './core/store/actions/auth.actions';
import { selectSetupData } from './core/store/selectors/common.selectors';
import { IAppState } from './store';
import { APP_VERSION } from './version';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('fade', [
      transition('void => active', [
        // using status here for transition
        style({ opacity: 0 }),
        animate(1000, style({ opacity: 1 })),
      ]),
      transition('* => void', [animate(1000, style({ opacity: 0 }))]),
    ]),
  ],
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
  preloader = true;
  animationImage = environment.animationImage;
  toggleLeftMenu: boolean;
  returnUrl: string;
  isDineIn: boolean = false;
  externalMember: ISearchedMemberModel = {} as ISearchedMemberModel;
  isShowMenu = {
    profile: false,
    location: false,
    department: false,
  };
  isShowPreorder: boolean = false;
  setupCurrency: string = '';
  languages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'ar', label: 'العربية' },
  ];
  dropdownOpen = false;

  currentLang =
    this.translate.currentLang || sessionStorage.getItem('lang') || 'en';

  constructor(
    private fbService: FirebaseService,
    public translate: TranslateService,
    private wheelSetupService: WheelSetupsService,
    private router: Router,
    private route: ActivatedRoute,
    store: Store<IAppState>
  ) {
    super(store);

    setTimeout(() => {
      this.preloader = false;
      if (this.addOn.WheelCustomScript) {
        this.appendCustomScript(this.addOn.WheelCustomScript);
      }
    }, 2000);

    translate.addLangs(['en', 'fr', 'ar']);
    translate.setDefaultLang('en');

    const savedLang = sessionStorage.getItem('lang');
    if (savedLang) {
      translate.use(savedLang);
    } else {
      const browserLang = translate.getBrowserLang();
      const lang = browserLang.match(/en|fr|ar/) ? browserLang : 'en';
      translate.use(lang);
      sessionStorage.setItem('lang', lang); // save it for next time
    }
  }

  ngOnInit() {
    this.checkVersion();
    this.router.events.subscribe((event) => {
      try {
        if (event instanceof NavigationEnd) {
          this.returnUrl = this.route.snapshot.queryParams['returnUrl'];
          var elem = document.getElementsByClassName('body')[0];
          if (event.url.indexOf('menu') > -1) {
            elem.classList.add('body-extend');
          } else {
            elem.classList.remove('body-extend');
          }
        }

        this.translate.get('signIn').subscribe((val) => {
          console.log('signIn translation:', val);

        });
      } catch (e) {
        console.log(e);
      }
    });

    this.wheelSetupService.externalMember$.subscribe((member) => {
      this.externalMember = member;
    });

    this.store
      .select(selectSetupData)
      .pipe(
        filter((f) => Boolean(f)),
        tap((r) => {
          this.setupCurrency = r.currency || 'SGD';
          this.isShowMenu['location'] = !(r.wheelLocations.length > 0);
          const addOns = JSON.parse(r.addOns);
          this.isShowPreorder = addOns.DisplayProfileInHomePage;
        })
      )
      .subscribe();

    this.wheelSetupService.data$.subscribe((data) => {
      if (data.label == 'dineIn') {
        this.isDineIn = data.value;
      } else {
        if (this.isLoggedIn) {
          this.isShowMenu['profile'] = true;
        }
      }
    });

    this.fbService.signInToFb();
    this.fbService.initializeNotification();
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  checkVersion() {
    const cachedVersion = localStorage.getItem('app-version');

    if (cachedVersion && cachedVersion !== APP_VERSION) {
      console.log(`Version changed: ${cachedVersion} → ${APP_VERSION}`);

      // Clear cache and reload
      localStorage.clear();
      sessionStorage.clear();
      localStorage.setItem('app-version', APP_VERSION);

      // Force hard reload (bypass cache)
      window.location.reload();
    } else {
      localStorage.setItem('app-version', APP_VERSION);
      console.log(`App version: ${APP_VERSION}`);
    }
  }

  gotoHome() {
    this.toggleLeftMenu = false;
    this.router.navigate([`/${this.tenantCode}`]);
  }

  gotoProfile() {
    this.toggleLeftMenu = false;
    this.router.navigateByUrl(`/${this.tenantCode}/account/user-profile`);
  }

  continue() {
    this.toggleLeftMenu = false;
    this.router.navigateByUrl(`/${this.tenantCode}/location`);
  }

  login() {
    this.toggleLeftMenu = false;
    this.router.navigate([`/${this.tenantCode}/sign-in`], {
      queryParams: { returnUrl: this.router.url },
    });
  }

  signUp() {
    this.toggleLeftMenu = false;
    this.router.navigate([`/${this.tenantCode}/sign-up`], {
      queryParams: { returnUrl: this.router.url },
    });
  }

  logout() {
    this.store.dispatch(logoutAction());
  }

  logoutExternalMember() {
    this.wheelSetupService.clearExternalMember();
  }

  gotoLocations() {
    this.toggleLeftMenu = false;
    this.router.navigate([`/${this.tenantCode}/location`]);
  }

  gotoDepartments() {
    this.toggleLeftMenu = false;
    this.router.navigate([
      `/${this.tenantCode}/location/${this.getLocationId()}/department`,
    ]);
  }

  toggleMenu() {
    if (sessionStorage.getItem(CONSTANT.AUTH) && !this.toggleLeftMenu) {
      const user = JSON.parse(sessionStorage.getItem(CONSTANT.AUTH));
      if (user) {
        this.store.dispatch(LoggedInAction({ user }));
      }
    }
    this.toggleLeftMenu = !this.toggleLeftMenu;
  }

  setLang(event) {
    const lang = event.target.value;
    sessionStorage.setItem('lang', lang);
    this.currentLang = lang;
    this.translate.use(lang);
    this.dropdownOpen = false;
  }
  changePassword() {
    var routerURL = null;
    if (this.router.url.includes('entity')) {
      const returnUrlStartIndex = this.router.url.indexOf('returnUrl=');
      if (returnUrlStartIndex !== -1) {
        let returnUrl = this.router.url.substring(returnUrlStartIndex + 10); // +10 to skip 'returnUrl='
        routerURL = decodeURIComponent(returnUrl);
      } else {
        routerURL = this.router.url;
      }
    }
    this.toggleLeftMenu = false;
    this.router.navigate([`/${this.tenantCode}/change-password`], {
      queryParams: { returnUrl: routerURL },
    });
  }

  ngOnDestroy() {
    this.fbService.signOut();
  }
}
